import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import { environment } from 'src/environments/environment';
import { CurriculumLevel, Curriculum, CurriculumStatistic, CurriculumTreeSetting, DailyPlanSettings, ObjectiveLevel, CurriculumSyllabusLevelSetting, CurriculumObjectivesSetting, Dashboard, DashboardDay } from '../../_models/index';
import { tap } from 'rxjs/operators';
import { LmsService } from './lms.service';
import { map } from 'rxjs/operators';
@Injectable({
  providedIn: 'root'
})
export class CurriculumService {


  private apiUrl = `${environment.apiUrl}/Curriculum`;
  constructor(private http: HttpClient) {
  }

  getBySubjectId(subjectId: number): Observable<Curriculum> {
    return this.http.get<Curriculum>(`${this.apiUrl}/GetBySubjectId/${subjectId}`);
  }

  getBySectionSubjectId(SectionSubjectId: number): Observable<Curriculum> {
    return this.http.get<Curriculum>(`${this.apiUrl}/GetBySectionSubjectId/${SectionSubjectId}`);
  }

  getCurriculumLevelsBySubjectId(subjectId: number): Observable<CurriculumLevel[]> {
    return this.http.get<CurriculumLevel[]>(`${this.apiUrl}/GetCurriculumLevelsBySubjectId/${subjectId}`);
  }

  getCurriculumLevelsBySectionSubjectId(sectionSubjectId: number): Observable<CurriculumLevel[]> {
    return this.http.get<CurriculumLevel[]>(`${this.apiUrl}/GetCurriculumLevelsBySectionSubjectId/${sectionSubjectId}`);
  }

  getObjectiveLevelsWithParentsBySubjectId(subjectId: number): Observable<ObjectiveLevel[]> {
    return this.http.get<ObjectiveLevel[]>(`${this.apiUrl}/GetObjectiveLevelsWithParentsBySubjectId/${subjectId}`);
  }

  getObjectiveLevelsBySectionSubjectId(sectionSubjectId: number): Observable<ObjectiveLevel[]> {
    return this.http.get<ObjectiveLevel[]>(`${this.apiUrl}/GetObjectiveLevelsBySectionSubjectId/${sectionSubjectId}`);
  }

  /////////////////////////////////

  initializeCurriculum(curriculumId: number): Observable<Curriculum> {
    return this.http.put<Curriculum>(`${this.apiUrl}/InitializeCurriculum/${curriculumId}`, {});
  }

  getCurriculumTreeSetting(subjectId: number): Observable<CurriculumTreeSetting> {
    return this.http.get<CurriculumTreeSetting>(`${this.apiUrl}/GetCurriculumTreeSetting/${subjectId}`);
  }

  updateCurriculumTreeSetting(curriculumTreeSetting: CurriculumTreeSetting): Observable<CurriculumTreeSetting> {

    return this.http
      .put<CurriculumTreeSetting>(`${this.apiUrl}/UpdateCurriculumTreeSetting`, curriculumTreeSetting)
      .pipe(tap(c => {
        LmsService.curriculumChanged$.next();
        return c;
      }));
  }

  updatePeriodSetting(curriculum: Curriculum): Observable<Curriculum> {
    return this.http
      .put<Curriculum>(`${this.apiUrl}/UpdatePeriodSetting`, curriculum)
      .pipe(tap(c => {
        LmsService.curriculumChanged$.next();
      }));
  }

  updateResourcesSetting(curriculum: Curriculum, password: string = null): Observable<Curriculum> {
    if (password)
      curriculum['password'] = password;

    return this.http
      .put<Curriculum>(`${this.apiUrl}/UpdateResourcesSetting`, curriculum)
      .pipe(tap(c => {
        LmsService.curriculumChanged$.next();
      }));
  }

  getCurriculumObjectivesSetting(subjectId: number): Observable<CurriculumObjectivesSetting> {
    return this.http.get<CurriculumObjectivesSetting>(`${this.apiUrl}/GetCurriculumObjectivesSetting/${subjectId}`);
  }

  updateObjectiveSetting(curriculumObjectivesSetting: CurriculumObjectivesSetting): Observable<CurriculumObjectivesSetting> {
    return this.http
      .put<CurriculumObjectivesSetting>(`${this.apiUrl}/UpdateObjectiveSetting`, curriculumObjectivesSetting)
      .pipe(tap(c => {
        LmsService.curriculumChanged$.next();
        return c;
      }));
  }

  getCurriculumSyllabusLevelSetting(subjectId: number): Observable<CurriculumSyllabusLevelSetting> {
    return this.http.get<CurriculumSyllabusLevelSetting>(`${this.apiUrl}/GetCurriculumSyllabusLevelSetting/${subjectId}`);
  }

  updateSyllabusSetting(curriculumSyllabusLevelSetting: CurriculumSyllabusLevelSetting): Observable<CurriculumSyllabusLevelSetting> {
    return this.http.put<CurriculumSyllabusLevelSetting>(`${this.apiUrl}/UpdateSyllabusSetting/`, curriculumSyllabusLevelSetting);
  }

  updateClassificationSetting(curriculum: Curriculum): Observable<Curriculum> {
    return this.http
      .put<Curriculum>(`${this.apiUrl}/UpdateClassificationSetting`, curriculum)
      .pipe(tap(c => {
        LmsService.curriculumChanged$.next();
        return c;
      }));
  }

  getDailyPlanSettingsBySubjectId(curriculumId: number): Observable<DailyPlanSettings> {
    return this.http.get<DailyPlanSettings>(`${this.apiUrl}/GetDailyPlanSettingsBySubjectId/${curriculumId}`);
  }

  getDailyPlanSettingsBySectionSubjectId(sectionSubjectId: number): Observable<DailyPlanSettings> {
    return this.http.get<DailyPlanSettings>(`${this.apiUrl}/GetDailyPlanSettingsBySectionSubjectId/${sectionSubjectId}`);
  }

  updateDailyPlanSettings(dailyPlanSettings: DailyPlanSettings): Observable<DailyPlanSettings> {
    return this.http.put<DailyPlanSettings>(`${this.apiUrl}/UpdateDailyPlanSettings/`, dailyPlanSettings);
  }

  /////////////////////////////////

  getSubjectCurriculumStatistic(subjectId: number, sectionSubjectId: number): Observable<CurriculumStatistic> {
    return this.http.get<CurriculumStatistic>(
      `${this.apiUrl}/GetSubjectCurriculumStatistic/${subjectId}${sectionSubjectId ? '/' + sectionSubjectId : ''}`);
  }

  getDashboardBySectionSubjectId(sectionSubjectId: number): Observable<Dashboard> {
    return this.http.get<Dashboard>(`${this.apiUrl}/GetDashboardBySectionSubjectId/${sectionSubjectId}`);
  }

  setWeeklyPlanPeriodCompleted(weeklyPlanPeriodId: number, sectionSubjectId: number): Observable<Dashboard> {
    return this.http.put<Dashboard>(`${this.apiUrl}/SetWeeklyPlanPeriodCompleted/${weeklyPlanPeriodId}/${sectionSubjectId}`, {});
  }

  getDashboardDaysBySectionSubjectIdAndSemesterPlanWeekId(sectionSubjectId: number, semesterPlanWeekId: number): Observable<DashboardDay[]> {
    return this.http
      .get<DashboardDay[]>(`${this.apiUrl}/getDashboardDaysBySectionSubjectIdAndSemesterPlanWeekId/${sectionSubjectId}/${semesterPlanWeekId}`)
      .pipe(map(d => {
        _.forEach(d, dd => this.setDashboardDayDates(dd));
        return d;
      }));
  }


  getStudentsBySectionSubjectIdWithNotesPaged(sectionSubjectId: number, pageIndex: number, pageSize: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/GetStudentsBySectionSubjectIdWithNotesPaged/${sectionSubjectId}/${pageIndex}/${pageSize}`);
  }

  // getByIdWithTopObjectivesThatHaveLastChildAndObjectivesLevels(curriculumId: number): Observable<Curriculum> {
  //   return this.http.get<Curriculum>(`${this.apiUrl}/GetByIdWithTopObjectivesThatHaveLastChildAndObjectivesLevels/${curriculumId}`);
  // }


  private setDashboardDayDates(dashboardDay: DashboardDay) {
    if (!dashboardDay)
      return;

    if ((typeof dashboardDay.date) === 'string' && String(dashboardDay.date).length > 0)
      dashboardDay.date = new Date(String(dashboardDay.date));
  }

}
